/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';

import parameters from './__generated__/RecommendedTabEntryPointRootQuery$parameters';
import type { RecommendedTabEntryPointRootQuery as RecommendedTabEntryPointRootQueryType } from './__generated__/RecommendedTabEntryPointRootQuery.graphql.ts';

// Inlined because entrypoint files restrict cross-package imports.
// Keep in sync with JfindForYouEmptyStateActions.tsx.
const hasMobileUserAgent = (): boolean =>
	/Android|iPhone|iPad|Mobil/i.test(globalThis.navigator?.userAgent ?? '');

type RecommendedTabEntryPointQueries = {
	recommendedTabQuery: RecommendedTabEntryPointRootQueryType;
};

type RecommendedTabEntryPointComponent = EntryPointComponent<
	RecommendedTabEntryPointQueries,
	{},
	{},
	{}
>;

// Inlined because entrypoint files restrict cross-package imports.
// NOTE: This logic must stay in sync with JfindForYouEmptyStateActions.get()
// in relay-providers/JfindForYouEmptyStateActions.tsx — keep both updated together.
const shouldIncludeGups = (accountId: string | null | undefined): boolean =>
	Boolean(accountId) && !hasMobileUserAgent() && fg('jfind_onboarding_actions_targeting_request');

// Inlined because entrypoint files restrict cross-package imports
// Keep aligned with NEEDS_INPUT_STATES in @jira/platform__agents-session-state-filters/src/AgentsSessionState.tsx.
const getNeedsInputAqlQuery = (atlassianAccountId: string): string =>
	`userAccountId = '${atlassianAccountId}' AND (state = 'UNKNOWN' OR state = 'INPUT_REQUIRED' OR state = 'AUTH_REQUIRED' OR state = 'REJECTED' OR state = 'FAILED' OR state = 'ARTEFACT_READY' OR state = 'ROVO_GENERATED_PLAN') AND deleted = 'false'`;

const getAgentSessionShardingContext = (cloudId: string, activationId: string): string =>
	`ari:cloud:jira:${cloudId}:workspace/${activationId}`;

export const recommendedTabEntryPoint: EntryPoint<
	RecommendedTabEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<RecommendedTabEntryPointComponent>(
		/* webpackChunkName: "async-jira-home-recommended-tab-entrypoint" */ './RecommendedTabEntryPointRoot.tsx',
	),
	getPreloadProps: ({ tenantContext }: EntryPointRouteParams) => {
		const { cloudId, atlassianAccountId, activationId } = tenantContext;
		const agentSessionVariables =
			atlassianAccountId != null && fg('jira-fy-recommended-assign-agent')
				? {
						aqlQuery: getNeedsInputAqlQuery(atlassianAccountId),
						shardingContext: getAgentSessionShardingContext(cloudId, activationId),
						first: 20,
					}
				: {};

		return {
			queries: {
				recommendedTabQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters,
					variables: {
						cloudId,
						userId: atlassianAccountId ?? '',
						includeGups: shouldIncludeGups(atlassianAccountId),
						...agentSessionVariables,
					},
				},
			},
			entryPoints: {},
			extraProps: {},
		};
	},
});
